import { motion } from 'framer-motion';
import { ReactNode, useLayoutEffect, useRef, useState } from 'react';

import { Tab, TabList } from '@bonusly/design-system';

const MotionBox = motion.div;

interface SecondaryTabsProps {
	tabIndex: number;
	setTabIndex: (index: number) => void;
	labels: (string | ReactNode)[];
}

export const SecondaryTabs: React.FC<SecondaryTabsProps> = ({ tabIndex, labels }) => {
	const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
	const [underline, setUnderline] = useState({ left: 0, width: 0 });

	useLayoutEffect(() => {
		const updateUnderline = () => {
			const currentTab = tabRefs.current[tabIndex];
			if (currentTab) {
				const { offsetLeft, clientWidth } = currentTab;
				setUnderline({ left: offsetLeft, width: clientWidth });
			}
		};

		// immediate update
		updateUnderline();

		// fallback update after content renders
		const timer = setTimeout(updateUnderline, 0);

		return () => clearTimeout(timer);
	}, [tabIndex, labels]);

	return (
		<TabList borderBottom="1px solid" borderColor="border.default" position="relative">
			{labels.map((label, index) => (
				<Tab
					key={index}
					ref={(el) => (tabRefs.current[index] = el)}
					height={14}
					fontWeight="medium"
					fontSize="md"
					color="text.softest"
					position="relative"
					px={3}
					// NOTE: SVG icons may've hardcoded stroke colors (e.g., stroke="#656C7B")
					// that need to be overridden to inherit the tab's text color states.
					// Using !important to override inline SVG attributes and ensure
					// icons change color on hover/selection to match text behavior.
					sx={{
						'& svg path': {
							stroke: 'currentColor !important',
						},
					}}
					_hover={{
						color: 'text.default',
						'& svg path': {
							stroke: 'currentColor !important',
						},
					}}
					_selected={{
						color: 'primary.fg',
						borderBottom: 'none',
						'& svg path': {
							stroke: 'currentColor !important',
						},
					}}
				>
					{label}
				</Tab>
			))}
			{underline.width > 0 && (
				<MotionBox
					layout
					transition={{
						type: 'spring',
						stiffness: 600,
						damping: 25,
					}}
					style={{
						position: 'absolute',
						bottom: -1,
						left: underline.left,
						width: underline.width,
						height: 4,
						backgroundColor: 'var(--chakra-colors-primary-fg)',
					}}
				/>
			)}
		</TabList>
	);
};
